import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePWD308Query } from './hooks/usePWD308Query';
import type { ReportRow } from '../../services/pwd308Api';
import './PWD308Form.css';

const PWD308Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = usePWD308Query();

  const currentYear = new Date().getFullYear();
  const [ymFrom, setYmFrom] = useState(`${currentYear}01`);
  const [ymTo, setYmTo] = useState(`${currentYear}12`);
  const [vendorFrom, setVendorFrom] = useState('');
  const [vendorTo, setVendorTo] = useState('');
  const [subFrom, setSubFrom] = useState('');
  const [subTo, setSubTo] = useState('');
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');

  const buildCmpYear = (ym: string) => {
    if (!ym || ym.length < 6) return '';
    const y = parseInt(ym.substring(0, 4), 10) - 1;
    return `${y}${ym.substring(4)}`;
  };

  const handleQuery = () => {
    runQuery({
      yearMonthFrom: ymFrom.trim() || undefined,
      yearMonthTo: ymTo.trim() || undefined,
      compYearFrom: buildCmpYear(ymFrom) || undefined,
      compYearTo: buildCmpYear(ymTo) || undefined,
      vendorFrom: vendorFrom.trim() || undefined,
      vendorTo: vendorTo.trim() || undefined,
      subcontractFrom: subFrom.trim() || undefined,
      subcontractTo: subTo.trim() || undefined,
      deptFrom: deptFrom.trim() || undefined,
      deptTo: deptTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setYmFrom(`${currentYear}01`);
    setYmTo(`${currentYear}12`);
    setVendorFrom('');
    setVendorTo('');
    setSubFrom('');
    setSubTo('');
    setDeptFrom('');
    setDeptTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>廠商年度託工數量</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>廠商年度託工數量</h2>
<table><thead><tr><th>廠商代號</th><th>工繳代號</th><th>工繳名稱</th><th>幣別</th><th>當年數量</th><th>當年金額</th><th>比較年數量</th><th>比較年金額</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.vendor_code}</td><td>${r.subcontract_code}</td><td>${r.subcontract_name}</td><td>${r.currency}</td><td class="right">${r.current_qty}</td><td class="right">${r.current_amt.toFixed(2)}</td><td class="right">${r.comp_qty}</td><td class="right">${r.comp_amt.toFixed(2)}</td></tr>`).join('')}
</tbody></table>
<p style="margin-top:8px">共 ${data.length} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<ReportRow> = [
    { title: '廠商代號', dataIndex: 'vendor_code', key: 'vendor_code', width: 110 },
    { title: '工繳代號', dataIndex: 'subcontract_code', key: 'subcontract_code', width: 90 },
    { title: '工繳名稱', dataIndex: 'subcontract_name', key: 'subcontract_name', ellipsis: true, width: 150 },
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 60 },
    { title: '當年數量', dataIndex: 'current_qty', key: 'current_qty', width: 90, align: 'right' as const },
    { title: '當年金額', dataIndex: 'current_amt', key: 'current_amt', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '比較年數量', dataIndex: 'comp_qty', key: 'comp_qty', width: 90, align: 'right' as const },
    { title: '比較年金額', dataIndex: 'comp_amt', key: 'comp_amt', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  return (
    <PermissionGuard module={Modules.PWD308} action="query">
      <div className="pwd308-container">
        <div className="pwd308-header">
          <h2>廠商年度託工數量</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>當年起 (YYYYMM)</label>
              <Input value={ymFrom} onChange={e => setYmFrom(e.target.value)} placeholder="例 202501" style={{ width: 120 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>當年迄 (YYYYMM)</label>
              <Input value={ymTo} onChange={e => setYmTo(e.target.value)} placeholder="例 202512" style={{ width: 120 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>廠商代號起</label>
              <Input value={vendorFrom} onChange={e => setVendorFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>廠商代號迄</label>
              <Input value={vendorTo} onChange={e => setVendorTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>工繳代號起</label>
              <Input value={subFrom} onChange={e => setSubFrom(e.target.value)} placeholder="起" style={{ width: 110 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>工繳代號迄</label>
              <Input value={subTo} onChange={e => setSubTo(e.target.value)} placeholder="迄" style={{ width: 110 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>部門起</label>
              <Input value={deptFrom} onChange={e => setDeptFrom(e.target.value)} placeholder="起" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>部門迄</label>
              <Input value={deptTo} onChange={e => setDeptTo(e.target.value)} placeholder="迄" style={{ width: 100 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <Table<ReportRow>
            columns={columns}
            dataSource={data}
            rowKey={(r, i) => `${r.vendor_code}-${r.subcontract_code}-${r.currency}-${i}`}
            loading={loading}
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
            scroll={{ x: 900 }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PWD308Form;
